// src/components/faq/FaqHero.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { Search } from 'lucide-react';
import clsx from 'clsx';

interface HeroData {
  headline: string;
  subheadline: string;
  searchPlaceholder: string;
}

interface FaqHeroProps {
  data: HeroData;
  searchQuery: string;
  onSearchChange: (query: string) => void;
}

export const FaqHero: React.FC<FaqHeroProps> = ({ data, searchQuery, onSearchChange }) => {
  if (!data) return null;

  return (
    <section className="bg-white py-16 md:py-20">
      <div className="container mx-auto px-4 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="max-w-3xl mx-auto"
        >
          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900">
            {data.headline}
          </h1>
          <p className="mt-4 text-lg text-slate-600">
            {data.subheadline}
          </p>
          
          {/* --- ANIMATION ENHANCEMENT --- */}
          {/* The outer div is a "group" to control the hover/focus state of its children */}
          <div className="mt-8 relative group">
            {/* Layer 1: The shadow. It's positioned absolutely behind the content. */}
            {/* On group-hover/focus, it translates to become visible. */}
            <div
              className={clsx(
                "absolute inset-0 bg-black rounded-xl",
                "transition-transform duration-200 ease-out",
                // Apply the shadow effect on hover OR when the input inside is focused
                "group-hover:translate-x-1 group-hover:translate-y-1",
                "group-focus-within:translate-x-1 group-focus-within:translate-y-1"
              )}
            />

            {/* Layer 2: The visible input container that moves on hover/focus */}
            <div className={clsx(
                "relative transition-transform duration-200 ease-out",
                "group-hover:-translate-x-1 group-hover:-translate-y-1",
                "group-focus-within:-translate-x-1 group-focus-within:-translate-y-1"
            )}>
              <Search
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10"
                size={20}
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={data.searchPlaceholder}
                // The input is now transparent but sits on top.
                // It has no shadow by default and no focus ring.
                className={clsx(
                  "relative w-full pl-11 pr-4 py-3 text-lg rounded-xl border-2 border-black",
                  "bg-white focus:outline-none"
                )}
                aria-label="Search FAQs"
              />
            </div>
          </div>
          {/* --- END OF ANIMATION ENHANCEMENT --- */}

        </motion.div>
      </div>
    </section>
  );
};

